import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PricingCard } from '@/components/billing/PricingCard';
import { PlanToggle } from '@/components/billing/PlanToggle';
import { useCreateCheckout } from '@/hooks/use-create-checkout';
import { useSubscription } from '@/hooks/use-subscription';
import { useEntitlements } from '@/hooks/use-entitlements';
import { BILLING_PLANS, getPriceId } from '@/config/billing';

export default function BillingPlans() {
  const [isAnnual, setIsAnnual] = useState(false);
  const { createCheckout, loading } = useCreateCheckout();
  const { isSubscribed, planName } = useSubscription();
  const { isFree, gracePeriodActive } = useEntitlements();
  const navigate = useNavigate();

  const handleSelectSolo = () => {
    const priceId = getPriceId('solo', isAnnual ? 'annual' : 'monthly');
    createCheckout(priceId);
  };

  const handleSelectPractice = () => {
    const priceId = getPriceId('practice', isAnnual ? 'annual' : 'monthly');
    createCheckout(priceId);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold">Choose Your Plan</h1>
          <p className="text-muted-foreground mt-2">
            {isSubscribed
              ? `You're currently on ${planName}. Select a plan to change.`
              : isFree && gracePeriodActive
                ? 'You have unlimited access during your free trial. Choose a plan for after.'
                : 'Upgrade to unlock unlimited reports, all templates, and clean exports.'}
          </p>
        </div>

        <PlanToggle isAnnual={isAnnual} onToggle={setIsAnnual} className="mb-10" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Solo */}
          <PricingCard
            name={BILLING_PLANS.solo.name}
            description={BILLING_PLANS.solo.description}
            price={isAnnual ? BILLING_PLANS.solo.annualPrice : BILLING_PLANS.solo.monthlyPrice}
            priceSubtext={isAnnual ? BILLING_PLANS.solo.priceSubtextAnnual : BILLING_PLANS.solo.priceSubtextMonthly}
            features={[
              BILLING_PLANS.solo.includedLimits.reports,
              BILLING_PLANS.solo.includedLimits.insight,
              BILLING_PLANS.solo.includedLimits.seats,
              ...BILLING_PLANS.solo.features,
            ]}
            highlighted
            badge="Most Popular"
            buttonText={isSubscribed ? 'Change Plan' : 'Subscribe'}
            onSelect={handleSelectSolo}
            loading={loading}
          />

          {/* Practice */}
          <PricingCard
            name={BILLING_PLANS.practice.name}
            description={BILLING_PLANS.practice.description}
            price={isAnnual ? BILLING_PLANS.practice.annualPrice : BILLING_PLANS.practice.monthlyPrice}
            priceSubtext={isAnnual ? BILLING_PLANS.practice.priceSubtextAnnual : BILLING_PLANS.practice.priceSubtextMonthly}
            features={[
              BILLING_PLANS.practice.includedLimits.reports,
              BILLING_PLANS.practice.includedLimits.insight,
              BILLING_PLANS.practice.includedLimits.seats,
              ...BILLING_PLANS.practice.features,
            ]}
            buttonText={isSubscribed ? 'Change Plan' : 'Subscribe'}
            onSelect={handleSelectPractice}
            loading={loading}
          />
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground">
            Need 20+ seats?{' '}
            <a href="mailto:sales@radiologylive.ai" className="text-primary hover:underline">
              Contact sales
            </a>{' '}
            for Enterprise pricing.
          </p>
        </div>
      </div>
    </div>
  );
}
